/**
 * This method is for get multiple feeds from an api list
 * because React dose not support use hook in a loop.
 *
 * @summary useMultipleFeedApi
 * @author MonkeyFigaro <monkeychen@sohu-inc.com>
 *
 * Created at     : 2019-04-02 11:32:45
 * Last modified  : 2019-08-14 22:52:25
 */

import { jsonGet } from '../../utils/http';
import { useState }  from 'react';
import { FeedInstance, FeedAPI } from '../../models/feed';
import { useEffectAsync } from '../useEffectAsync';
import { sleep } from '../../utils';

/**
 * @param  {number|string} api - Pass an regionID
 * @param  {number} slice - The max length of the feed list
 * @returns [FeedInstances, doFetch, loading, error]
 */
export const useMultipleFeedApi = (regionIds: Array<number | string>, slices: number[]): [FeedInstance[][], Array<() => void>, boolean[], boolean[]] => {
    const [feeds, setFeeds]: [FeedInstance[][], any] = useState<FeedInstance[][]>([]);
    const [loadings, setLoadings]: [boolean[], any] = useState(Array(regionIds.length).fill(true));
    const [errors, setErrors]: [boolean[], any] = useState(Array(regionIds.length).fill(false));
    /**
     * request state
     * default [1, 1, ...., 1]
     * `0` - stands for `Request end`
     * `1` -  stands for `Will make the first request`
     * `2` - stands for `make request manually`
     */
    const [requests, setRequests]: [Array<0 | 1 | 2>, any] = useState<Array<0 | 1 | 2>>(Array(regionIds.length).fill(1));

    const doFetches: Array<() => void> = [];

    const stateMap = {
        'feeds': [feeds, setFeeds],
        'loadings': [loadings, setLoadings],
        'errors': [errors, setErrors],
        'requests': [requests, setRequests],
    };

    const updateValue = (stateName: string, index: number, value: any) => {
        const [state, setState] = stateMap[stateName];
        state[index] = value;
        setState([...state]);
    };

    regionIds.forEach((id, index) => {
        useEffectAsync(async () => {
            if (requests[index] === 0) {
                // Stop requests again when switch
                return;
            }
            updateValue('requests', index, 0);
            const url = `//v2.sohu.com/integration-api/mix/region/${id}`;

            if (errors[index]) {
                updateValue('errors', index, false);
            }
            if (!loadings[index]) {
                updateValue('loadings', index, true);
            }
            if (requests[index] === 2) {
                await sleep(1000);
            }
            let res: FeedAPI;
            try {
                res = await jsonGet(url) as FeedAPI;
            } catch (err) {
                res = err;
            }
            if (res.status === 200 && res.body.data && res.body.data.length) {
                updateValue('feeds', index, res.body.data.filter(e => !e.backup && e.url).slice(0, slices[index]));
            } else {
                updateValue('errors', index, true);
            }
            updateValue('loadings', index, false);

        }, [id, slices[index], requests[index]]);
        doFetches[index] = () => {
            updateValue('requests', index, 2);
        };
    });

    return [feeds, doFetches, loadings, errors];
};



// WEBPACK FOOTER //
// ./src/hooks/APIs/useMultipleFeedApi.ts